if (!customElements.get('mega-menu-header')) {
  // iOS Safari never fires `:active` (and so never runs the nudge
  // animation, which is keyed off `:active` for taps) unless *some*
  // touch listener exists on the page -- a long-standing WebKit quirk,
  // not a bug in the nudge CSS itself. A single no-op listener is the
  // standard fix and is harmless if the theme already has one elsewhere.
  document.addEventListener('touchstart', function () {}, { passive: true });

  class MegaMenuHeader extends HTMLElement {
    connectedCallback() {
      this.breakpoint = parseInt(this.dataset.breakpoint || '1024', 10);
      this.navItems = Array.from(this.querySelectorAll('.mmh-nav-item--mega'));
      this.openCount = 0;

      this._flagMobileColumnPanels();
      this._bindMegaMenuItems();
      this._applyFixedOffset();
      window.addEventListener('resize', () => this._applyFixedOffset());

      document.addEventListener('click', (e) => this._onDocumentClick(e));
      document.addEventListener('keydown', (e) => this._onKeydown(e));
      window.addEventListener('resize', () => this._syncOpenState());
    }

    _isMobile() {
      return window.innerWidth < this.breakpoint;
    }

    // A panel's own "does this item have dedicated mobile columns" flag
    // can't be computed in Liquid (block.blocks doesn't reliably expose a
    // block's own nested children for introspection), so it's done here
    // instead: plain querySelector/classList, supported by every browser
    // old enough to run this dropdown at all -- unlike CSS :has(), which
    // needs a 2022+ browser. This runs once, synchronously, before any
    // interaction, so the CSS split (see mega-menu-header.css) is correct
    // from first paint.
    _flagMobileColumnPanels() {
      this.querySelectorAll('.mmh-panel').forEach((panel) => {
        if (panel.querySelector('.mmh-mcol')) {
          panel.classList.add('mmh-panel--has-mobile-columns');
        }
      });
    }
        _flagMobileColumnPanels() {
      this.querySelectorAll('.mmh-panel').forEach((panel) => {
        if (panel.querySelector('.mmh-mcol')) {
          panel.classList.add('mmh-panel--has-mobile-columns');
        }
      });
    }

    // position:fixed (used by .mmh-header--sticky / --force-sticky, see
    // mega-menu-header.css) pulls the header out of the page flow, so
    // whatever comes after it in the page would sit underneath it unless
    // something pushes it down by the header's own height. Measuring the
    // real rendered height here (instead of a guessed px value) keeps this
    // correct across the different mobile/desktop header heights and
    // whatever the merchant has tuned in settings.
    _applyFixedOffset() {
      const isFixed = this.classList.contains('mmh-header--sticky') ||
                      this.classList.contains('mmh-header--force-sticky');
      document.body.style.paddingTop = isFixed ? `${this.getBoundingClientRect().height}px` : '';
    }

    /* ---------- Mega menu items (shared desktop + mobile) ----------
       An item can render ONE panel (desktop content reused on mobile) or
       TWO (a dedicated ".mmh-panel--mobile-only" alongside the regular
       ".mmh-panel--desktop"). Either way, open/close only ever toggles the
       "is-open" class on the <li> -- CSS decides which panel (if any) is
       actually visible for the current breakpoint. That keeps this method
       correct regardless of how many panels a given item has. */
    _bindMegaMenuItems() {
      this.navItems.forEach((item) => {
        const trigger = item.querySelector(':scope > .mmh-nav-trigger');
        const panels = Array.from(item.querySelectorAll(':scope > .mmh-panel'));
        if (!trigger) return;

        let closeTimer = null;
        const clearCloseTimer = () => {
          if (closeTimer) {
            window.clearTimeout(closeTimer);
            closeTimer = null;
          }
        };

        const open = () => {
          clearCloseTimer();
          this._closeAllMegaMenus(item);
          item.classList.add('is-open');
          trigger.setAttribute('aria-expanded', 'true');
          this.openCount += 1;
          this._syncOpenState();
        };
        const close = () => {
          clearCloseTimer();
          if (!item.classList.contains('is-open')) return;
          item.classList.remove('is-open');
          trigger.setAttribute('aria-expanded', 'false');
          this.openCount = Math.max(0, this.openCount - 1);
          this._syncOpenState();
        };
        const scheduleClose = () => {
          clearCloseTimer();
          // Grace period so moving the pointer from the trigger down to the
          // panel (across the visual gap between them) doesn't close it
          // before the pointer arrives -- cancelled by mouseenter on either.
          closeTimer = window.setTimeout(close, 250);
        };

        // Hover only applies on desktop; on mobile these listeners still
        // attach but no-op via the _isMobile() guard, so tap-to-toggle
        // (below) is the only way in on touch devices. Binding to a panel
        // that's currently display:none (e.g. the mobile-only one while on
        // desktop) is harmless -- a hidden element never receives the event.
        [trigger, ...panels].forEach((el) => {
          el.addEventListener('mouseenter', () => {
            if (!this._isMobile()) open();
          });
          el.addEventListener('mouseleave', () => {
            if (!this._isMobile()) scheduleClose();
          });
        });

        trigger.addEventListener('click', () => {
          if (trigger.getAttribute('aria-expanded') === 'true') {
            close();
          } else {
            open();
          }
        });

        item._mmhOpen = open;
        item._mmhClose = close;
      });
    }

    _closeAllMegaMenus(exceptItem) {
      this.navItems.forEach((item) => {
        if (item !== exceptItem && item._mmhClose) item._mmhClose();
      });
    }

    // While any panel is open on mobile: force the header sticky (so the
    // panel, which docks to the header's own bottom edge, always lands
    // directly under it regardless of scroll position or the merchant's
    // own sticky setting) and lock body scroll (the panel can be taller
    // than the viewport and scrolls internally instead).
    _syncOpenState() {
      const anyOpen = this.openCount > 0;
      const mobileOpen = anyOpen && this._isMobile();
      this.classList.toggle('mmh-header--force-sticky', mobileOpen);
      document.documentElement.classList.toggle('mmh-scroll-lock', mobileOpen);
    }

    _onDocumentClick(e) {
      if (!this.contains(e.target)) this._closeAllMegaMenus();
    }

    _onKeydown(e) {
      if (e.key === 'Escape') this._closeAllMegaMenus();
    }
  }
  customElements.define('mega-menu-header', MegaMenuHeader);
}
